import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Download, Loader2, Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogClose } from '@/components/ui/Dialog';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';
import { useAuth } from '@/lib/auth/useAuth';
import { queryKeys } from '@/lib/api/queryKeys';
import type { XkStock } from '@/lib/api/types';
import { cn } from '@/lib/cn';
import {
  parseImportZip,
  summarizeParse,
  type ConflictDecision,
  type ParsedImport,
} from './parseImportZip';
import {
  runImport,
  type ImportProgress as ImportProgressShape,
  type ImportSummary,
} from './runImport';
import { ImportPreviewTable } from './ImportPreviewTable';
import { ImportProgressView } from './ImportProgress';
import { downloadTemplate } from './downloadTemplate';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  existingStock: XkStock[];
}

type UiPhase = 'pick' | 'parsing' | 'preview' | 'running' | 'done';

export function BulkImportDialog({ open, onOpenChange, existingStock }: Props) {
  const t = useT();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [phase, setPhase] = useState<UiPhase>('pick');
  const [parsed, setParsed] = useState<ParsedImport | null>(null);
  const [progress, setProgress] = useState<ImportProgressShape | null>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setPhase('pick');
      setParsed(null);
      setProgress(null);
      setSummary(null);
    }
  }, [open]);

  const handleFile = useCallback(
    async (file: File) => {
      setPhase('parsing');
      const result = await parseImportZip(file, existingStock);
      setParsed(result);
      if (result.fatalError) {
        toast({
          tone: 'error',
          title: t('stock.import.parseFailed'),
          description: t(`stock.import.error.${result.fatalError}`),
        });
        setPhase('pick');
        return;
      }
      setPhase('preview');
    },
    [existingStock, t],
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) void handleFile(file);
    },
    [handleFile],
  );

  const onPick = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) void handleFile(file);
      e.target.value = '';
    },
    [handleFile],
  );

  const setDecision = useCallback(
    (rowIndex: number, decision: ConflictDecision) => {
      setParsed((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          rows: prev.rows.map((r) =>
            r.rowIndex === rowIndex ? { ...r, decision } : r,
          ),
        };
      });
    },
    [],
  );

  const stats = useMemo(() => (parsed ? summarizeParse(parsed) : null), [parsed]);
  const canExecute = !!parsed && parsed.totalErrors === 0 && parsed.rows.length > 0;

  const onExecute = useCallback(async () => {
    if (!parsed) return;
    setPhase('running');
    setProgress({
      phase: 'uploadingImages',
      imagesDone: 0,
      imagesTotal: 0,
      rowsDone: 0,
      rowsTotal: 0,
    });
    try {
      const result = await runImport({
        parsed,
        companyId: user?.companyId ?? undefined,
        onProgress: setProgress,
      });
      setSummary(result);
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.plans.current });
      setPhase('done');

      const tone = result.failed === 0 ? 'success' : result.failed === result.rows.length ? 'error' : 'info';
      const title =
        result.failed === 0
          ? t('stock.import.summarySuccess')
              .replace('{created}', String(result.created))
              .replace('{updated}', String(result.updated))
          : result.failed === result.rows.length
            ? t('stock.import.summaryAllFailed').replace('{count}', String(result.rows.length))
            : t('stock.import.summaryMixed')
                .replace('{created}', String(result.created))
                .replace('{updated}', String(result.updated))
                .replace('{failed}', String(result.failed));
      toast({ tone, title });
    } catch (err) {
      toast({
        tone: 'error',
        title: t('stock.import.parseFailed'),
        description: err instanceof Error ? err.message : String(err),
      });
      setPhase('preview');
    }
  }, [parsed, queryClient, t, user?.companyId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="scrollbar-thin max-h-[90vh] max-w-[860px] overflow-y-auto p-0">
        <div className="p-8">
          <header className="mb-6">
            <div
              className="mb-2 text-[11px] font-extrabold uppercase tracking-[0.10em] text-[var(--color-accent-ink)]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {t('stock.import.title')}
            </div>
            <h2
              className="mb-1.5 text-[22px] font-black tracking-tight text-[var(--color-xk-lavender)]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {t('stock.import.title')}
            </h2>
            <p className="text-[13px] text-[var(--color-xk-text-muted)]">
              {t('stock.import.subtitle')}
            </p>
          </header>

          {phase === 'pick' && (
            <PickStep
              dragOver={dragOver}
              setDragOver={setDragOver}
              onDrop={onDrop}
              inputRef={inputRef}
              onPick={onPick}
            />
          )}

          {phase === 'parsing' && (
            <div className="flex items-center justify-center gap-2 py-12 text-[13px] text-[var(--color-xk-text-muted)]">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('stock.import.parsing')}
            </div>
          )}

          {phase === 'preview' && parsed && stats && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[var(--color-xk-text-muted)]">
                  {t('stock.import.preview')}
                </div>
                <div className="text-[11px] text-[var(--color-xk-text-muted)]">
                  {t('stock.import.previewCount')
                    .replace('{count}', String(parsed.rows.length))
                    .replace('{errors}', String(stats.errors))}
                </div>
              </div>
              <ImportPreviewTable
                rows={parsed.rows}
                imageBlobs={parsed.imageBlobs}
                onDecisionChange={setDecision}
              />
            </div>
          )}

          {phase === 'running' && progress && (
            <div className="py-6">
              <ImportProgressView progress={progress} />
            </div>
          )}

          {phase === 'done' && summary && (
            <DoneStep summary={summary} />
          )}

          <div className="mt-6 flex items-center justify-between gap-3 border-t border-[var(--color-xk-border)] pt-5">
            <button
              type="button"
              onClick={() => void downloadTemplate()}
              className="inline-flex items-center gap-1.5 text-[12px] font-bold text-[var(--color-accent-ink)] hover:underline"
            >
              <Download className="h-3.5 w-3.5" />
              {t('stock.import.downloadTemplate')}
            </button>
            <div className="flex gap-3">
              {phase !== 'running' && (
                <DialogClose asChild>
                  <button
                    type="button"
                    className="rounded-lg border border-[var(--color-xk-border)] px-5 py-2.5 text-[13px] font-bold text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-surface)]"
                  >
                    {phase === 'done' ? t('stock.import.done') : t('stock.import.cancel')}
                  </button>
                </DialogClose>
              )}
              {phase === 'preview' && parsed && (
                <button
                  type="button"
                  onClick={onExecute}
                  disabled={!canExecute}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-xk-accent)] px-5 py-2.5 text-[13px] font-extrabold text-[var(--color-fg-on-accent)] transition-colors hover:bg-[var(--color-xk-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Upload className="h-3.5 w-3.5" />
                  {t('stock.import.execute').replace('{count}', String(parsed.rows.length))}
                </button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PickStep({
  dragOver,
  setDragOver,
  onDrop,
  inputRef,
  onPick,
}: {
  dragOver: boolean;
  setDragOver: (v: boolean) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onPick: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  const t = useT();
  return (
    <div
      onDrop={onDrop}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onClick={() => inputRef.current?.click()}
      className={cn(
        'cursor-pointer rounded-xl border-2 border-dashed px-6 py-14 text-center transition-colors',
        dragOver
          ? 'border-[var(--color-accent-ink)] bg-[var(--color-accent-10)]'
          : 'border-[var(--color-xk-border)] hover:border-[var(--color-accent-ink)] hover:bg-[var(--color-xk-surface)]/40',
      )}
    >
      <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-[var(--color-accent-10)] text-[var(--color-accent-ink)]">
        <Upload className="h-5 w-5" />
      </div>
      <div className="text-[14px] font-bold text-[var(--color-xk-lavender)]">
        {t('stock.import.dropzone')}
      </div>
      <div className="mt-1 text-[11px] text-[var(--color-xk-text-muted)]">
        {t('stock.import.dropzoneHint')}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".zip,application/zip,application/x-zip-compressed"
        onChange={onPick}
        className="hidden"
      />
    </div>
  );
}

function DoneStep({ summary }: { summary: ImportSummary }) {
  const t = useT();
  const failed = summary.rows.filter((r) => r.outcome === 'failed');
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Created" value={summary.created} tone="success" />
        <Stat label="Updated" value={summary.updated} tone="info" />
        <Stat label="Failed" value={summary.failed} tone={summary.failed > 0 ? 'error' : 'muted'} />
      </div>
      {failed.length > 0 && (
        <details className="rounded-lg border border-[var(--color-xk-border)] p-3 text-[12px]">
          <summary className="cursor-pointer font-bold text-[var(--color-error)]">
            {t('stock.import.status.error')} ({failed.length})
          </summary>
          <ul className="mt-2 space-y-1 text-[11px] text-[var(--color-xk-text-muted)]">
            {failed.map((r) => (
              <li key={r.rowIndex}>
                <span className="font-bold text-[var(--color-xk-lavender)]">{r.name}</span>
                {' — '}
                {r.errorMessage ?? 'unknown error'}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'success' | 'info' | 'error' | 'muted';
}) {
  const color =
    tone === 'success'
      ? 'var(--color-success)'
      : tone === 'info'
        ? 'var(--color-xk-accent)'
        : tone === 'error'
          ? 'var(--color-error)'
          : 'var(--color-xk-text-muted)';
  return (
    <div className="rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)]/30 p-4 text-center">
      <div className="text-[24px] font-black" style={{ color, fontFamily: 'var(--font-display)' }}>
        {value}
      </div>
      <div className="mt-1 text-[10px] font-extrabold uppercase tracking-[0.14em] text-[var(--color-xk-text-muted)]">
        {label}
      </div>
    </div>
  );
}
